/**
 * Round 80 — A-C-12 Governance & Explainability
 *
 * Static methodology page. Documents what each intelligence surface
 * measures, the exact thresholds (pulled from `thresholds.ts` so they
 * cannot drift), confidence rules, and the allowed/forbidden language
 * list. Linked from every `ExplanationPopover` footer.
 */
import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollText, Gauge, GraduationCap, LineChart, Sparkles, ShieldCheck } from 'lucide-react';
import {
  BEFORE_AFTER_WINDOW_N,
  DIAGNOSTIC_DELTA_MIN_PER_SIDE,
  DIAGNOSTIC_MIN_SAMPLE_FOR_DISPLAY,
  DIAGNOSTIC_MIN_SAMPLE_PER_TECH,
  DIAGNOSTIC_OUTCOME_SCORE,
  FIT_CONFIDENCE_RULES,
  FIT_SIGNAL_THRESHOLDS,
  FIT_SUGGESTION_GATE,
  NO_CHANGE_DELTA,
  OBSERVED_IMPACT_CONFIDENCE,
  TRAINING_VISIBILITY_NOTE,
} from '@/lib/intelligence-explanations/thresholds';
import { ALLOWED_PHRASES, FORBIDDEN_PHRASES } from '@/lib/intelligence-explanations/terminology';
import { logIntelligenceView } from '@/lib/intelligence-explanations/audit';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';

export default function EnterpriseIntelligenceGovernance() {
  useEffect(() => {
    logIntelligenceView({
      action_type: 'view_analytics',
      entity_type: 'page',
      entity_id: 'governance',
    });
  }, []);

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-4">
      <BackToOptiNetworkButton testId="enterprise-governance-back-to-opti-network" />
      <div>
        <div className="flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold">Intelligence Methodology</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Plain-English documentation of every metric, threshold, and confidence rule
          used across OptiBay's enterprise intelligence surfaces. Read-only.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Talent Analytics (R74)</CardTitle>
          </div>
          <CardDescription>
            Operational metrics computed from completed-ticket data already in the system.
            Descriptive only — no predictions, no scoring.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Section
            title="Efficiency"
            body="Average ratio of predicted minutes to actual minutes across a technician's completed jobs. Computed per ticket then averaged."
          />
          <Section
            title="Comeback rate"
            body="Share of a technician's completed jobs flagged as comebacks."
          />
          <Section
            title="Diagnostic accuracy"
            body={`Average of recorded diagnostic outcome scores: Confirmed=${DIAGNOSTIC_OUTCOME_SCORE.Confirmed}, Partial=${DIAGNOSTIC_OUTCOME_SCORE.Partial}, Misdiagnosis=${DIAGNOSTIC_OUTCOME_SCORE.Misdiagnosis}. Per-row value requires at least ${DIAGNOSTIC_MIN_SAMPLE_PER_TECH} outcomes; scope summary requires at least ${DIAGNOSTIC_MIN_SAMPLE_FOR_DISPLAY}.`}
          />
          <Section
            title="Distribution bands"
            body="Simple quartile sort of the filtered table. Top quartile = top efficiency band, bottom quartile = development band. No hidden weights."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Training Visibility (R75)</CardTitle>
          </div>
          <CardDescription>{TRAINING_VISIBILITY_NOTE}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Section
            title="Total training events"
            body="Count of recorded training engagement events across the scope."
          />
          <Section
            title="Technicians engaged"
            body="Count of distinct technicians with at least one recorded event."
          />
          <Section
            title="Avg artifacts per technician"
            body="Distinct artifacts engaged with, averaged across engaged technicians."
          />
          <Section
            title="Most active format"
            body="Artifact format with the highest engagement count in scope."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <LineChart className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Observed Impact (R76)</CardTitle>
          </div>
          <CardDescription>
            Observational comparison of completed jobs each technician finished before vs.
            after engaging with an artifact. Correlation only — not causation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Section
            title="Window size"
            body={`${BEFORE_AFTER_WINDOW_N} completed tickets on each side per technician.`}
          />
          <Section
            title="No-change band"
            body={`Absolute deltas below ${NO_CHANGE_DELTA} are reported as no significant change.`}
          />
          <Section
            title="Diagnostic delta"
            body={`Requires at least ${DIAGNOSTIC_DELTA_MIN_PER_SIDE} diagnostic outcomes on each side per technician, otherwise omitted.`}
          />
          <Section
            title="Confidence buckets"
            body={`${OBSERVED_IMPACT_CONFIDENCE.low.label}; ${OBSERVED_IMPACT_CONFIDENCE.medium.label}; ${OBSERVED_IMPACT_CONFIDENCE.high.label}.`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Fit Suggestions (R78)</CardTitle>
          </div>
          <CardDescription>
            Advisory only. Five independent boolean signals. No composite score, no ML.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Section
            title="High efficiency"
            body={`Triggered when avg efficiency ≥ ${FIT_SIGNAL_THRESHOLDS.high_efficiency.min_eff} across ≥ ${FIT_SIGNAL_THRESHOLDS.high_efficiency.min_jobs} completed jobs.`}
          />
          <Section
            title="Low comeback rate"
            body={`Triggered when comeback rate ≤ ${FIT_SIGNAL_THRESHOLDS.low_comeback_rate.max_rate * 100}% across ≥ ${FIT_SIGNAL_THRESHOLDS.low_comeback_rate.min_jobs} completed jobs.`}
          />
          <Section
            title="Strong diagnostic accuracy"
            body={`Triggered when diagnostic accuracy ≥ ${FIT_SIGNAL_THRESHOLDS.strong_diagnostic_accuracy.min_acc * 100}% across ≥ ${FIT_SIGNAL_THRESHOLDS.strong_diagnostic_accuracy.min_outcomes} outcomes.`}
          />
          <Section
            title="High job volume"
            body={`Triggered when completed jobs ≥ ${FIT_SIGNAL_THRESHOLDS.high_job_volume.min_jobs}.`}
          />
          <Section
            title="Recent training activity"
            body={`Triggered when ≥ ${FIT_SIGNAL_THRESHOLDS.recent_training_activity.min_events} training event in the last ${FIT_SIGNAL_THRESHOLDS.recent_training_activity.window_days} days.`}
          />
          <Section
            title="Display gate"
            body={`Suggestion appears only when signal_count ≥ ${FIT_SUGGESTION_GATE.min_signals} AND completed jobs ≥ ${FIT_SUGGESTION_GATE.min_jobs}.`}
          />
          <Section
            title="Confidence rule"
            body={`High: ${FIT_CONFIDENCE_RULES.high}. Medium: ${FIT_CONFIDENCE_RULES.medium}. Low: ${FIT_CONFIDENCE_RULES.low}.`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Language discipline</CardTitle>
          </div>
          <CardDescription>
            Every explanation summary uses neutral, observational language. The list
            below is the contract; a dev-build guard warns when forbidden terms slip in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Allowed
            </p>
            <div className="flex flex-wrap gap-1.5">
              {ALLOWED_PHRASES.map((p) => (
                <Badge key={p} variant="outline" className="text-xs">
                  {p}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Forbidden
            </p>
            <div className="flex flex-wrap gap-1.5">
              {FORBIDDEN_PHRASES.map((p) => (
                <Badge key={p} variant="outline" className="text-xs text-destructive border-destructive/40">
                  {p}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <p className="font-medium">{title}</p>
      <p className="text-muted-foreground text-xs mt-0.5">{body}</p>
    </div>
  );
}
